import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, Award, BookOpen, Key, ArrowRight, Clock, Users,
  CheckCircle2, Building2, Filter, GitCompare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getProgramsByCategory, PROGRAM_CATEGORIES } from "@/data/programCatalog";
import ExecutiveHero from "@/components/ExecutiveHero";

const PROGRAMS_HERO = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=2400&q=80"; // executive graduation / academic procession

export default function Programs() {
  const [activeFilter, setActiveFilter] = useState("all");

  const categories = [
    {
      id: "executive-professional-masters",
      icon: GraduationCap,
      title: "Executive Professional Masters",
      description: "Advanced leadership and workforce intelligence programs designed for senior executives and organizational leaders seeking to transform their workforce capabilities.",
      duration: "12-18 months",
      format: "Hybrid (Online + Residencies)",
      tuition: "$24,000 – $36,000",
      prerequisites: "Bachelor's degree + 5 yrs leadership experience",
      outcomes: ["Strategic workforce transformation", "AI-era leadership credential", "Capstone-driven institutional change project"],
      features: ["Executive Cohort Model", "Capstone Project", "Industry Mentorship"],
      color: "#1E3A8A"
    },
    {
      id: "training-certificates",
      icon: Award,
      title: "Training Certificates",
      description: "Professional certification programs in workforce intelligence, AI integration, quality management, and organizational development.",
      duration: "3-6 months",
      format: "Online / Blended",
      tuition: "$1,800 – $4,500",
      prerequisites: "Professional working experience recommended",
      outcomes: ["Industry-recognized credential", "Applied workforce intelligence toolkit", "Digital badge + verified certificate"],
      features: ["Industry-Recognized Credentials", "Practical Assessments", "Digital Badges"],
      color: "#2563EB"
    },
    {
      id: "courses",
      icon: BookOpen,
      title: "Courses",
      description: "Focused courses covering specific topics in workforce readiness, AI applications, leadership, safety, and operational excellence.",
      duration: "4-12 weeks",
      format: "Self-Paced / Instructor-Led",
      tuition: "$295 – $1,200",
      prerequisites: "Open enrollment — no formal prerequisites",
      outcomes: ["Topic-specific competency", "Completion certificate", "Continuing-education credit (select courses)"],
      features: ["Flexible Scheduling", "Expert Instructors", "Completion Certificates"],
      color: "#3B82F6"
    },
    {
      id: "licensing",
      icon: Key,
      title: "Licensing",
      description: "Professional licensing programs for organizations and individuals seeking to implement Zodiac workforce intelligence methodologies.",
      duration: "Varies",
      format: "Institutional Partnership",
      tuition: "Custom — request a proposal",
      prerequisites: "Organization-level engagement",
      outcomes: ["Methodology licensing", "Co-branded training delivery", "Ongoing institutional support"],
      features: ["Methodology Access", "Training Materials", "Ongoing Support"],
      color: "#60A5FA"
    }
  ];

  // Filter categories based on active filter
  const filteredCategories = activeFilter === "all" 
    ? categories 
    : categories.filter(c => c.id === activeFilter);

  const filters = [
    { id: "all", label: "All Programs" },
    { id: "executive-professional-masters", label: "Executive Professional Masters" },
    { id: "training-certificates", label: "Training Certificates" },
    { id: "courses", label: "Courses" },
    { id: "licensing", label: "Licensing Programs" }
  ];

  return (
    <div className="min-h-screen zodiac-page" data-testid="programs-page">
      <ExecutiveHero
        eyebrow="Professional Education"
        title={<>Programs &</>}
        accentTitle="executive education."
        subtitle="Develop workforce intelligence capabilities through our integrated suite — executive masters, professional certificates, focused courses, and institutional licensing."
        image={PROGRAMS_HERO}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "Programs" }]}
        actions={[
          { label: "Apply Now", href: "/programs/apply", testId: "programs-hero-apply" },
        ]}
        testId="programs-hero"
      />

      {/* Filter System */}
      <section className="py-6 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-5 h-5 text-[#1E3A8A]" />
            {filters.map((filter) => (
              <Button
                key={filter.id}
                variant={activeFilter === filter.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(filter.id)}
                className={activeFilter === filter.id 
                  ? "bg-[#1E3A8A] text-white hover:bg-[#1E40AF]" 
                  : "border-[#E5E7EB] text-[#1E3A8A] hover:bg-[#F8FAFC]"
                }
                data-testid={`filter-${filter.id}`}
              >
                {filter.label}
              </Button>
            ))}
            <div className="flex-1" />
            <Link to="/programs/compare">
              <Button variant="outline" size="sm" className="border-[#E5E7EB] text-[#1E3A8A] gap-2">
                <GitCompare className="w-4 h-4" />
                Compare Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredCategories.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-6 pb-4" style={{ borderBottom: `3px solid ${program.color}` }}>
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${program.color}15` }}
                        >
                          <program.icon className="w-7 h-7" style={{ color: program.color }} />
                        </div>
                        <div>
                          <h2 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                            {program.title}
                          </h2>
                          <p className="text-[#1E3A8A] text-sm leading-relaxed">
                            {program.description}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="p-6 pt-4 bg-[#F8FAFC]">
                      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                        <Detail icon={Clock} label="Duration" value={program.duration} />
                        <Detail icon={Building2} label="Format" value={program.format} />
                        <Detail icon={Award} label="Tuition" value={program.tuition} />
                        <Detail icon={Users} label="Prerequisites" value={program.prerequisites} />
                      </div>

                      {program.outcomes && program.outcomes.length > 0 && (
                        <div className="mb-5">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#1E3A8A]/60 mb-2">Outcomes</p>
                          <ul className="space-y-1.5">
                            {program.outcomes.map((o, oi) => (
                              <li key={oi} className="flex items-start gap-2 text-sm text-[#1E3A8A]">
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: program.color }} />
                                <span>{o}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Link to={`/programs/${program.id}`} className="flex-1">
                          <Button variant="outline" className="w-full border-[#E5E7EB] text-[#1E3A8A] hover:bg-white" data-testid={`learn-more-${program.id}`}>
                            Learn More
                          </Button>
                        </Link>
                        <Link to={`/programs/apply?program=${program.id}`} className="flex-1">
                          <Button className="w-full gap-2 text-white" style={{ backgroundColor: program.color }} data-testid={`apply-now-${program.id}`}>
                            Apply Now
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Ready to Get Started?
            </h2>
            <p className="text-lg text-[#1E3A8A] mb-8">
              Explore our admissions process and take the first step toward advancing your workforce intelligence capabilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/admissions">
                <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
                  View Admissions
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="h-12 px-8 border-[#E5E7EB]">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[#1E3A8A]/50 font-semibold">{label}</p>
        <p className="text-sm text-[#1E3A8A] leading-tight">{value}</p>
      </div>
    </div>
  );
}
